import * as React from "react";
import {cx} from "emotion";

import "./dragging-rect.scss";

import {cs}            from "@Common/react/chain-services";
import {csArr}         from "@Common/react/cs-arr";
import {keyed}         from "@Common/react/keyed";
import {chain, chain2} from "@Common/utils/fs";
import {keepOnly}      from "@Common/utils/objects";

import {addVector}      from "../../../common/add-vector";
import {Dragging}       from "../../../common/dragging";
import {inSize}         from "../../../common/in-size";
import {multiplyRect}   from "../../../common/multiply-rect";
import {multiplyVector} from "../../../common/multiply-vector";
import {roundVector}    from "../../../common/round-vector";
import {subtractSize}   from "../../../common/subtract-size";
import {toCssPos}       from "../../../common/to-css-pos";
import {rectToLines}    from "../common/rect-to-lines";

import {Corner}     from "./corner/corner";
import {OuterShade} from "./outer-shade";
import {toCssRect}  from "./to-css-rect";

export const DraggingRect = ({rect, outSize, onChangedRect, adjPoints, getBgColor, frame}) => cs(
    ["corners", (_, next) => csArr(
        [0,90,180,270].map((rotation) => (
            (next) => Corner({
                rotation,
                adjPoints,
                onChangedRect: (fn) => onChangedRect(fn(rect)),
                next,
            })
        )),
        next
    )],
    ["middleBox", (_, next) => MiddleBox({
        outSize,
        onChangedRect: (fn) => onChangedRect(fn(rect)),
        next,
    })],
    ["rect", ({corners, middleBox}, next) => next(chain2(
        rect,
        [...corners.map((c) => c.modRect), middleBox.modRect].filter(v=>v),
    ))],
    ["lines", ({rect}, next) => next(chain(
        rect,
        (_) => rectToLines(_),
    ))],
    ({corners, middleBox, rect, lines}) => (
        <div {...{
            className: "dragging-rect-9r7",
            style: {
                ...toCssPos(frame.start),
                ...{width: 0, height: 0},
            },
        }}>
            {middleBox.render({
                ratio: frame.ratio,
                rect,
            })}

            {OuterShade({
                rect, outSize,
                style: {
                    ...toCssRect({x:0,y:0, ...frame.size}),
                },
            })}
            {corners.map((corner, i) => cs(
                keyed(i),
                () => corner.render({
                    limitSize: keepOnly(rect, ["width", "height"]),
                    start: lines[i].start,
                    frame,
                    getBgColor,
                }),
            ))}
        </div>
    ),
);

const MiddleBox = ({outSize, onChangedRect, next}) => cs(
    ["dragging", (_, next) => Dragging({next})],
    ({dragging}) => {
        const modRect = dragging.active?.delta && (
            (rect) => ({
                ...rect,
                ...inSize(
                    addVector(rect, dragging.active.delta),
                    subtractSize(outSize, rect),
                ),
            })
        );
        return next({
            modRect,
            render: ({rect, ratio}) => (
                <div {...{
                    className: cx("middle-box", {grabbing: dragging.active}),
                    ...dragging.handle,
                    style: toCssRect(multiplyRect(rect, ratio)),
                }}>
                    {dragging.renderActive({
                        modDelta: (delta) => chain(
                            multiplyVector(delta, 1 / ratio),
                            roundVector,
                        ),
                        onDone: () => onChangedRect(modRect),
                    })}
                </div>
            ),
        });
    },
);
